import { useMemo, useState } from 'react';
import { Button, Icon, LoadingCircle, TextInputHelper, Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { debounce } from 'lodash';
import { Modal } from 'react-bootstrap';
import { StyleSheet } from 'react-native';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import colors from '@packages/core/styles/colors';

const dropTextMainStyle = { marginBottom: 8 };

export type RunCvtDiagnosticProps = {
    /** Whether the file is being uploaded and run. */
    isUploading: boolean;

    /** Takes the user back to select their concerns. */
    onBack: () => void;

    /** Uploads the file and runs the diagnostic. Rejects with why it failed. */
    onRunDiagnostic: (file: File, repairOrderNumber: string) => Promise<void>;
};

type FileState = {
    loading: boolean;
    file: File | null;
    successfully: boolean;
    diagnosticId?: string;
};

const initialState: FileState = {
    loading: false,
    file: null,
    successfully: false,
};

export const REPAIR_ORDER_NUMBER_ERROR_MESSAGE =
    'Repair Order Missing. You must enter a valid Repair Order (RO) number before running a CVT diagnostic.';

const useStyles = createUseStyles({
    dropZone: {
        alignItems: 'center',
        border: `1px solid ${colors.grayFive}`,
        borderRadius: 4,
        cursor: 'pointer',
        display: 'flex',
        height: 144,
        justifyContent: 'center',
    },
    uploadedItem: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '2rem',
        padding: 20,
    },
    box: {
        border: `1px solid ${colors.grayFive}`,
        borderRadius: 4,
    },
});

const styles = StyleSheet.create({
    repairOrder: {
        marginTop: 24,
    },
});

/**
 * The modal's content for uploading the CVT file and running the diagnostic.
 *
 * Renders the file drop zone, the Repair Order field, and an Upload & Run button, enabled once both are filled in.
 */
export function RunCvtDiagnostic({ isUploading, onBack, onRunDiagnostic }: RunCvtDiagnosticProps) {
    const classes = useStyles();
    const { t } = useTranslation();
    const [fileState, setFileState] = useState<FileState>(initialState);
    const [showFileTypeError, setShowFileTypeError] = useState<boolean>(false);

    const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
    const [repairOrderNumber, setRepairOrderNumber] = useState('');
    const [uploadErrorMessage, setUploadErrorMessage] = useState<string>('');

    function handleFileUpload(file: File) {
        setFileState((prev) => ({ ...prev, loading: true, successfully: false, file }));

        debounce(
            async () => {
                setFileState((prev) => ({
                    ...prev,
                    loading: false,
                    successfully: true,
                }));
            },
            100,
            { leading: true, trailing: false }
        )();
    }

    async function handleUploadButtonClick() {
        if (!fileState.file) {
            return;
        }

        setHasAttemptedSubmit(true);
        setUploadErrorMessage('');

        try {
            await onRunDiagnostic(fileState.file, repairOrderNumber);
        } catch (error) {
            setFileState((prev) => ({ ...prev, successfully: false }));
            setUploadErrorMessage(error.message);
        }

        setRepairOrderNumber('');
        setHasAttemptedSubmit(false);
    }

    const handleDrop = (accepted: File[]) => {
        const file = accepted[0];
        if (file) {
            setFileState((prev) => ({ ...prev, file }));
            setShowFileTypeError(false);
            setUploadErrorMessage('');
            handleFileUpload(file);
        }
    };

    function handleRejected(rejectedFiles: File[]) {
        if (rejectedFiles.some((file) => file.type !== 'text/csv')) {
            setShowFileTypeError(true);
        }
    }

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: handleDrop,
        onDropRejected: handleRejected,
        accept: '.csv',
        multiple: false,
        disabled: fileState.loading || isUploading,
    });

    const fileSize = useMemo(
        () => (fileState.file ? (fileState.file.size / 1024 / 1024).toFixed(3) : '0'),
        [fileState.file]
    );

    return (
        <>
            <Modal.Header closeButton={!isUploading}>
                <Modal.Title>{t('cvt:runDiagnosticModal.title', 'Run Diagnostic')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div {...getRootProps()} className={`${classes.dropZone} ${isDragActive ? 'bg-light' : 'bg-white'}`}>
                    <input {...getInputProps()} />
                    <div className="text-center">
                        <div className="d-flex  justify-content-center mb-3">
                            {fileState.loading ? <LoadingCircle /> : <Icon color="blueOne" name="export" size={24} />}
                        </div>
                        <div className="d-flex flex-column">
                            <Typography style={dropTextMainStyle}>
                                {fileState.loading
                                    ? t('cvt:runDiagnosticModal.uploading', 'Uploading file...')
                                    : fileState.file
                                      ? t('cvt:runDiagnosticModal.uploaded', 'Click or drag to replace current file')
                                      : t('cvt:runDiagnosticModal.placeholder', 'Click or drag and drop')}
                            </Typography>
                            <Typography color="textDarkTertiary" variant="caption">
                                {t('cvt:runDiagnosticModal.hint', 'CSV File Only')}
                            </Typography>
                        </div>
                    </div>
                </div>

                {showFileTypeError && (
                    <div className="mt-2" role="alert">
                        <Typography color="redOne" variant="caption">
                            Invalid file format. Only .csv files are allowed.
                        </Typography>
                    </div>
                )}

                {fileState.file && !fileState.loading && (
                    <div className={`${classes.uploadedItem} ${classes.box}`}>
                        <div className="d-flex flex-column gap-2">
                            <Typography variant="label">{fileState.file.name}</Typography>
                            <Typography color="textDarkSecondary" variant="caption">{`${fileSize} MB`}</Typography>
                        </div>
                        <Icon
                            name={fileState.successfully ? 'check-circle-hollow' : 'x-circle'}
                            color={fileState.successfully ? 'greenOne' : 'redOne'}
                        />
                    </div>
                )}

                <TextInputHelper
                    style={styles.repairOrder}
                    value={repairOrderNumber}
                    label="Repair Order"
                    onChangeText={setRepairOrderNumber}
                    placeholder="Enter RO Number"
                    required
                    errorMessage={hasAttemptedSubmit && !repairOrderNumber ? REPAIR_ORDER_NUMBER_ERROR_MESSAGE : ''}
                    testID="repair-order-input"
                />

                {uploadErrorMessage && (
                    <div className="mt-2" role="alert">
                        <Typography color="redOne" variant="caption">
                            {uploadErrorMessage}
                        </Typography>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer>
                <div className="d-flex justify-content-between w-100">
                    <Button
                        disabled={isUploading}
                        iconLeft={<Icon color="blueOne" name="chevron-left" size={18} />}
                        onPress={onBack}
                        variant="ghost-blue"
                    >
                        {t('buttons:back', 'Back')}
                    </Button>
                    <Button
                        variant="primary"
                        onPress={handleUploadButtonClick}
                        disabled={fileState.loading || isUploading || !fileState.successfully || !repairOrderNumber}
                        isLoading={isUploading}
                        testID="upload-run-button"
                    >
                        {t('cvt:runDiagnosticModal.buttons.run', 'Upload & Run')}
                    </Button>
                </div>
            </Modal.Footer>
        </>
    );
}
